import { useMutation, useQueryClient } from '@tanstack/react-query';
import { strapiUpdateManagedUser } from '../../api/strapiUsers';
import { queryKeys } from '../queryKeys';

/** Bulk-assign the same `user_type` to many managed users (sequential PUT /api/users/:id). */
export function useBulkUpdateManagedUserTypeMutation(jwt: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userIds,
      userTypeId,
    }: {
      userIds: string[];
      userTypeId: string;
    }) => {
      if (!jwt) throw new Error('Not signed in');
      if (userIds.length === 0) throw new Error('Select at least one user.');
      if (!userTypeId.trim()) throw new Error('User type is required.');

      const failures: { id: string; message: string }[] = [];
      for (const id of userIds) {
        try {
          await strapiUpdateManagedUser(jwt, id, { userTypeId: userTypeId.trim() });
        } catch (err) {
          failures.push({
            id,
            message: err instanceof Error ? err.message : 'Unable to update user.',
          });
        }
      }

      if (failures.length > 0) {
        await qc.invalidateQueries({ queryKey: [...queryKeys.strapi, 'users'] });
        const preview = failures
          .slice(0, 3)
          .map((f) => `${f.id}: ${f.message}`)
          .join('; ');
        const more = failures.length > 3 ? ` (+${failures.length - 3} more)` : '';
        throw new Error(
          `Updated ${userIds.length - failures.length} of ${userIds.length} users. Failed: ${preview}${more}`,
        );
      }

      return { updatedCount: userIds.length };
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [...queryKeys.strapi, 'users'] });
    },
  });
}
